import type { PrV2FaviconVisualState } from './types';

import { PR_V2_FAVICON_HREF_BY_STATE } from './favicon-visual-state';

const MANAGED_ATTR = 'data-cubic-pr-v2-favicon-managed';
const CREATED_ATTR = 'data-cubic-pr-v2-favicon-created';
const STATE_ATTR = 'data-cubic-pr-v2-favicon-state';
const ORIGINAL_HREF_ATTR = 'data-cubic-pr-v2-original-href';
const ORIGINAL_TYPE_ATTR = 'data-cubic-pr-v2-original-type';
const ORIGINAL_SIZES_ATTR = 'data-cubic-pr-v2-original-sizes';
// `null` cannot round-trip through HTML attributes, so this sentinel lets us distinguish
// "attribute was missing before we managed the favicon" from a real string value.
const UNSET_ATTR_VALUE = '__cubic_pr_v2_unset__';
const DEFAULT_APP_FAVICON_HREF = '/favicon.ico';
const DEFAULT_APP_FAVICON_TYPE = 'image/x-icon';

const MANAGED_FAVICON_ATTRIBUTES = [
  ['href', ORIGINAL_HREF_ATTR],
  ['type', ORIGINAL_TYPE_ATTR],
  ['sizes', ORIGINAL_SIZES_ATTR],
] as const;

const METADATA_ATTRIBUTES = [MANAGED_ATTR, CREATED_ATTR, STATE_ATTR] as const;

function setOrRemoveAttribute(element: Element, name: string, value: string | null) {
  if (!value || value === UNSET_ATTR_VALUE) {
    element.removeAttribute(name);
    return;
  }

  element.setAttribute(name, value);
}

function backupFaviconAttributes(link: HTMLLinkElement) {
  for (const [attributeName, backupAttributeName] of MANAGED_FAVICON_ATTRIBUTES) {
    if (link.hasAttribute(backupAttributeName)) {
      continue;
    }

    link.setAttribute(backupAttributeName, link.getAttribute(attributeName) ?? UNSET_ATTR_VALUE);
  }
}

function restoreFaviconAttributes(link: HTMLLinkElement) {
  for (const [attributeName, backupAttributeName] of MANAGED_FAVICON_ATTRIBUTES) {
    setOrRemoveAttribute(link, attributeName, link.getAttribute(backupAttributeName));
    link.removeAttribute(backupAttributeName);
  }

  for (const attributeName of METADATA_ATTRIBUTES) {
    link.removeAttribute(attributeName);
  }
}

function getOrCreateFaviconLinks(): HTMLLinkElement[] {
  const links = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
  if (links.length > 0) {
    return links;
  }

  const link = document.createElement('link');
  link.setAttribute('rel', 'icon');
  link.setAttribute(CREATED_ATTR, 'true');
  document.head.append(link);
  return [link];
}

function ensureDefaultAppFavicon() {
  const existingIconLink = document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]');

  if (existingIconLink) {
    return;
  }

  // PR v2 can create a temporary favicon link when the page had no icon tags at all. Restoring
  // the app favicon here keeps non-PR routes from being left without any icon after cleanup.
  const link = document.createElement('link');
  link.setAttribute('rel', 'icon');
  link.setAttribute('href', DEFAULT_APP_FAVICON_HREF);
  link.setAttribute('type', DEFAULT_APP_FAVICON_TYPE);
  document.head.append(link);
}

export function applyPrV2ManagedFavicon(state: PrV2FaviconVisualState) {
  const links = getOrCreateFaviconLinks();
  const faviconHref = PR_V2_FAVICON_HREF_BY_STATE[state];

  for (const link of links) {
    backupFaviconAttributes(link);
    link.setAttribute(MANAGED_ATTR, 'true');
    link.setAttribute(STATE_ATTR, state);
    link.setAttribute('href', faviconHref);
    link.setAttribute('type', 'image/svg+xml');
    link.setAttribute('sizes', 'any');
  }
}

export function restorePrV2ManagedFavicon() {
  const links = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>(`link[${MANAGED_ATTR}="true"]`),
  );

  for (const link of links) {
    if (link.getAttribute(CREATED_ATTR) === 'true') {
      link.remove();
      continue;
    }

    restoreFaviconAttributes(link);
  }

  ensureDefaultAppFavicon();
}
